import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'
// Phase 5D — listen to the logout action so the per-user giveaway cache
// doesn't leak across sessions on the same browser. Safe to import: the
// auth reducer does NOT import from this file, so there's no cycle.
import { customer_logout, user_reset } from './authReducer'

// ── Thunks ──────────────────────────────────────────────────
export const get_giveaways = createAsyncThunk(
    'giveaway/list',
    async ({ page = 1, limit = 12, status = '', organizerType = '', search = '' } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const params = new URLSearchParams()
            params.set('page', page)
            params.set('limit', limit)
            if (status) params.set('status', status)
            if (organizerType) params.set('organizerType', organizerType)
            if (search) params.set('search', search)
            const { data } = await api.get(`/giveaways?${params.toString()}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message })
        }
    }
)

export const get_giveaway_detail = createAsyncThunk(
    'giveaway/detail',
    async (slug, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/giveaway/${encodeURIComponent(slug)}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message })
        }
    }
)

export const participate_giveaway = createAsyncThunk(
    'giveaway/participate',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post(`/giveaway/${id}/participate`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message })
        }
    }
)

// ── Phase 5C — Customer dashboard "Çəkilişlərim" + per-giveaway status ─
// Both endpoints are auth-gated. The dashboard list is always called from a
// protected route, but `get_my_giveaway_status` is also dispatched from the
// public detail page — there we silently absorb 401 (guests just don't get
// the "Qoşulmusunuz" badge until they log in).
export const get_my_giveaways = createAsyncThunk(
    'giveaway/myList',
    async ({ page = 1, limit = 12, filter = 'all' } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const params = new URLSearchParams()
            params.set('page', page)
            params.set('limit', limit)
            if (filter && filter !== 'all') params.set('filter', filter)
            const { data } = await api.get(`/customer/giveaways/my?${params.toString()}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message })
        }
    }
)

export const get_my_giveaway_status = createAsyncThunk(
    'giveaway/myStatus',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/giveaway/${id}/me/status`)
            return fulfillWithValue({ id, ...data })
        } catch (error) {
            // Guests / non-logged-in callers receive 401 — surface a quiet
            // "not participated" payload so the UI can render normally.
            const status = error.response?.status
            if (status === 401) {
                return fulfillWithValue({
                    id, participated: false, joinedAt: null,
                    isWinner: false, prizeIndex: null, prizeTitle: '',
                    resultStatus: 'unknown', _silent: true,
                })
            }
            return rejectWithValue(error.response?.data || { error: error.message })
        }
    }
)

// ── Slice ───────────────────────────────────────────────────
const giveawaySlice = createSlice({
    name: 'giveaway',
    initialState: {
        giveaways:        [],
        total:            0,
        page:             1,
        totalPages:       1,
        currentGiveaway:  null,
        loader:           false,
        detailLoader:     false,
        participateLoader: false,
        hasParticipated:  false,
        successMessage:   '',
        errorMessage:     '',
        // ── Phase 5C — customer dashboard + per-giveaway status cache ───
        myGiveaways:      [],
        myTotal:          0,
        myPage:           1,
        myTotalPages:     1,
        myLoader:         false,
        myError:          '',
        myFilter:         'all',
        // Keyed by giveawayId → { participated, isWinner, resultStatus, … }.
        // Lets us answer "is the current user already in?" without re-fetching.
        myStatusByGiveawayId: {},
        statusLoader:     false,
    },
    reducers: {
        messageClear: (s) => { s.successMessage = ''; s.errorMessage = '' },
        clearGiveaway: (s) => { s.currentGiveaway = null; s.hasParticipated = false },
        // Used on logout so a stale "Qoşulmusunuz" doesn't leak to the next
        // session signed in on the same browser.
        clearMyGiveawayStatus: (s) => { s.myStatusByGiveawayId = {}; s.myGiveaways = [] },
    },
    extraReducers: (builder) => {
        builder
            .addCase(get_giveaways.pending,   (s) => { s.loader = true })
            .addCase(get_giveaways.fulfilled, (s, { payload }) => {
                s.loader = false
                s.giveaways = payload.giveaways || []
                s.total = payload.total || 0
                s.page = payload.page || 1
                s.totalPages = payload.totalPages || 1
            })
            .addCase(get_giveaways.rejected,  (s) => { s.loader = false })

            .addCase(get_giveaway_detail.pending,   (s) => { s.detailLoader = true; s.hasParticipated = false })
            .addCase(get_giveaway_detail.fulfilled, (s, { payload }) => {
                s.detailLoader = false
                s.currentGiveaway = payload.giveaway || null
            })
            .addCase(get_giveaway_detail.rejected,  (s, { payload }) => {
                s.detailLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Çəkiliş tapılmadı'
            })

            .addCase(participate_giveaway.pending,   (s) => { s.participateLoader = true; s.errorMessage = '' })
            .addCase(participate_giveaway.fulfilled, (s, { payload }) => {
                s.participateLoader = false
                s.successMessage = payload.message || 'Çəkilişə qoşuldunuz'
                s.hasParticipated = true
                // Optimistic increment for the detail view.
                if (s.currentGiveaway && !payload.alreadyParticipating) {
                    s.currentGiveaway = {
                        ...s.currentGiveaway,
                        participantCount: (s.currentGiveaway.participantCount || 0) + 1,
                    }
                }
            })
            .addCase(participate_giveaway.rejected,  (s, { payload }) => {
                s.participateLoader = false
                s.errorMessage = payload?.error || payload?.message || 'İştirak qeydə alınmadı'
            })

            // ── Phase 5C ────────────────────────────────────────────────
            .addCase(get_my_giveaways.pending,   (s) => { s.myLoader = true; s.myError = '' })
            .addCase(get_my_giveaways.fulfilled, (s, { payload }) => {
                s.myLoader = false
                s.myGiveaways  = payload.giveaways || []
                s.myTotal      = payload.total || 0
                s.myPage       = payload.page || 1
                s.myTotalPages = payload.totalPages || 1
                s.myFilter     = payload.filter || 'all'
                // Warm the per-giveaway status cache so detail pages opened
                // from the dashboard don't need a second round-trip.
                for (const row of (payload.giveaways || [])) {
                    if (row?.giveawayId) {
                        s.myStatusByGiveawayId[row.giveawayId] = {
                            participated: true,
                            joinedAt: row.joinedAt,
                            isWinner: !!row.isWinner,
                            prizeIndex: row.prizeIndex ?? null,
                            prizeTitle: row.prizeTitle || '',
                            resultStatus: row.resultStatus,
                        }
                    }
                }
            })
            .addCase(get_my_giveaways.rejected,  (s, { payload }) => {
                s.myLoader = false
                s.myError = payload?.error || payload?.message || 'Çəkilişlər yüklənmədi'
            })

            .addCase(get_my_giveaway_status.pending,   (s) => { s.statusLoader = true })
            .addCase(get_my_giveaway_status.fulfilled, (s, { payload }) => {
                s.statusLoader = false
                const { id, _silent, ...rest } = payload || {}
                if (id) s.myStatusByGiveawayId[id] = rest
                // Mirror the boolean onto the participate-button flag so the
                // existing detail-page UI keeps working without rewriting.
                if (id && s.currentGiveaway && String(s.currentGiveaway._id) === String(id)) {
                    s.hasParticipated = !!rest.participated
                }
            })
            .addCase(get_my_giveaway_status.rejected,  (s) => { s.statusLoader = false })

            // ── Phase 5D — cross-session cleanup ────────────────────────
            // Wipe per-user caches on logout AND on user_reset (used by the
            // dashboard logout helper before the thunk resolves). The public
            // giveaway list/detail caches are intentionally kept — they're
            // not user-scoped and survive the session boundary.
            .addCase(customer_logout.fulfilled, (s) => {
                s.myGiveaways = []
                s.myTotal = 0
                s.myPage = 1
                s.myTotalPages = 1
                s.myStatusByGiveawayId = {}
                s.hasParticipated = false
            })
            .addCase(user_reset, (s) => {
                s.myGiveaways = []
                s.myTotal = 0
                s.myPage = 1
                s.myTotalPages = 1
                s.myStatusByGiveawayId = {}
                s.hasParticipated = false
            })
    },
})

export const { messageClear, clearGiveaway, clearMyGiveawayStatus } = giveawaySlice.actions
export default giveawaySlice.reducer
